/**
 * The pieces of a summary row Mappings → Clients and Mappings → Techs share
 * (summaryRows.ts): a service's status chip, the arrow that opens a row, and
 * what opens under it.
 */
import type { ReactNode } from "react";

import { chipCellClass, STATUS_CLASS, type Status } from "./summaryRows";

export function StatusChip({ status }: { status: Status | null }) {
  if (status === null) {
    return <span className="text-sm text-muted">…</span>;
  }
  return (
    <span className={`rounded-pill px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status.tone]}`}>
      {status.label}
    </span>
  );
}

/**
 * A service's chip in a summary row: under its column's heading, or, where
 * the row is a card, under the service's own name. `className` adds the
 * share of a card's line it takes.
 */
export function ChipCell({ name, status, className }: { name: string; status: Status | null; className: string }) {
  return (
    <td className={`${chipCellClass} ${className}`}>
      {/* The column's heading, where there are no columns. */}
      <span className="mb-1 block text-xs text-muted lg:hidden">{name}</span>
      <StatusChip status={status} />
    </td>
  );
}

export function OpenArrow({
  open,
  name,
  controls,
  onOpen,
}: {
  open: boolean;
  name: string;
  controls: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      aria-label={`${open ? "Hide" : "Show"} ${name}`}
      className={
        "inline-flex size-9 cursor-pointer items-center justify-center rounded-pill " +
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink " +
        (open ? "bg-card text-brand" : "text-muted group-hover:text-brand")
      }
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={
          "transition-transform motion-reduce:transition-none " +
          (open ? "rotate-90" : "group-hover:translate-x-0.5")
        }
      >
        <path d="M7 4l5 5-5 5" />
      </svg>
    </button>
  );
}

/** What opens under a summary row, spanning every column, the arrow's included. */
export function OpenedRow({
  columns,
  rowClassName,
  cellClassName,
  children,
}: {
  columns: number;
  rowClassName?: string;
  cellClassName?: string;
  children: ReactNode;
}) {
  return (
    <tr className={rowClassName}>
      <td
        colSpan={columns}
        className={
          "border-t border-status-in-progress-line bg-brand-tint/50 px-4 pt-4 pb-5 sm:pl-10" +
          (cellClassName === undefined ? "" : ` ${cellClassName}`)
        }
      >
        {children}
      </td>
    </tr>
  );
}
